/**
 * billingService — the customer-facing half of CYV-native billing
 * (autopay slice 1: payment-method collection, NO CHARGES).
 *
 * Two callables and one read. The callables are authoritative: the server
 * creates the Stripe Customer, opens the hosted setup form, and records the
 * result from the signature-verified webhook. Clients never write
 * `billingProfiles` — the rules deny it, the Admin SDK in Functions bypasses.
 *
 * NOTHING HERE CAN TAKE MONEY. The Checkout session this opens is
 * `mode:'setup'`: no amount, no line item, no PaymentIntent. It collects and
 * validates a card for FUTURE use. The live MMS billing path — which is what
 * is actually charging families today, on Anthony's manual monthly invoice —
 * is untouched by any of this and stays that way until the Aug 31 cutover.
 */

import { db, functions } from '../firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export const BILLING_PROFILES = 'billingProfiles';

/**
 * Open the hosted card form. Returns the Stripe URL to redirect to — the
 * caller navigates, rather than this function doing it, so a component can
 * show its own "opening secure form…" state and handle failure in place.
 *
 * `origin` is passed explicitly and validated server-side against the same
 * allowlist the workshop/pack flows use; a caller cannot redirect a family
 * anywhere but our own domains.
 */
export async function createBillingSetupSession(familyId) {
	const callable = httpsCallable(functions, 'createBillingSetupSession');
	const res = await callable({
		origin: window.location.origin,
		...(familyId ? { familyId } : {}),
	});
	return res.data; // { url, sessionId }
}

/**
 * Remove the card on file. Detaches at Stripe first, then clears our record —
 * see the callable for why that order. Returns { outcome, ... }; key UI copy
 * on `outcome`, never on an optimistic local guess.
 */
export async function removeBillingPaymentMethod(familyId) {
	const callable = httpsCallable(functions, 'removeBillingPaymentMethod');
	const res = await callable(familyId ? { familyId } : {});
	return res.data;
}

/**
 * Live-read a family's billing profile.
 *
 * THE DOC ID IS THE familyId — the payer is the family, not the auth user
 * (docs/PRODUCT.md → Core Ownership Model). The read rule grants exactly
 * `familyId == myFamilyId()`, so a guardian reading their own document needs
 * no query and no index; an unclaimed customer has no familyId and must not
 * call this at all.
 *
 * Returns null while the family has never started setup — which is the
 * expected state for every family right now, and is an empty state rather
 * than an error.
 */
export function subscribeToBillingProfile(familyId, onData, onError) {
	if (!familyId) {
		onData(null);
		return () => {};
	}
	return onSnapshot(
		doc(db, BILLING_PROFILES, familyId),
		(snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
		(err) => {
			console.error('[billing] profile subscription failed:', err);
			if (onError) onError(err);
		}
	);
}

export async function fetchBillingProfile(familyId) {
	if (!familyId) return null;
	const snap = await getDoc(doc(db, BILLING_PROFILES, familyId));
	return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * "Visa •••• 4242". Mirrors formatPaymentMethodLabel in functions/billing.js
 * deliberately rather than importing it — that module is CommonJS and
 * Functions-only, and a shared package for one string is not worth the build
 * complexity. A test pins the two against each other.
 */
export function formatPaymentMethodLabel(summary) {
	if (!summary) return 'No payment method on file';
	const str = (v) => typeof v === 'string' && v.length > 0;
	const isBank = summary.type === 'us_bank_account';
	const name = isBank
		? str(summary.bankName)
			? summary.bankName
			: 'Bank account'
		: str(summary.brand)
		? summary.brand.charAt(0).toUpperCase() + summary.brand.slice(1)
		: 'Card';
	if (!str(summary.last4)) return name;
	return `${name} •••• ${summary.last4}`;
}

// "Checking account" / "Savings account" — only meaningful for ACH, and shown
// instead of an expiry line, which a bank account does not have.
export function formatAccountKind(summary) {
	if (!summary || summary.type !== 'us_bank_account') return null;
	if (summary.accountType === 'checking') return 'Checking account';
	if (summary.accountType === 'savings') return 'Savings account';
	return 'Bank account';
}

export const AUTOPAY_PENDING_VERIFICATION = 'pending_verification';

// A bank account whose microdeposits have not cleared yet. Not an error and
// not a finished state — the family has to come back and confirm two small
// amounts, and until they do nothing can be debited.
export function isPendingVerification(profile) {
	return !!profile && profile.autopayStatus === AUTOPAY_PENDING_VERIFICATION;
}

// "Expires 4/2030" — null-safe, because a non-card method carries no expiry.
export function formatExpiry(summary) {
	if (!summary || !summary.expMonth || !summary.expYear) return null;
	return `Expires ${summary.expMonth}/${summary.expYear}`;
}

/**
 * Is this card expiring before the school year ends?
 *
 * Tuition runs Sept 1 → June 1 on ten installments, so a card that dies in
 * January is a failed payment in January — worth telling a family in August,
 * while it is a 30-second fix, rather than at the moment a charge declines.
 * Compares against the LAST installment date; pass it in rather than reading a
 * clock here so the check is testable.
 */
export function isExpiringBeforeSeasonEnd(summary, seasonEndDate) {
	if (!summary || !summary.expMonth || !summary.expYear) return false;
	// A card is valid through the END of its expiry month.
	const cardDeadline = new Date(summary.expYear, summary.expMonth, 1); // 1st of the following month
	return cardDeadline <= seasonEndDate;
}
